import { AbstractClient } from './AbstractClient';
import FormData from 'isomorphic-form-data';
import {
  AttributionSource,
  AuthUpdateClientPayload,
  ClientAddress,
  ClientResponse,
  CreateClientRequest,
  DocumentUpload,
  DocumentUploadResponse,
  Driver,
  DriversResponse,
  LineType,
  OwlResponse,
  PersonalInfo,
  PolicyCancellationRequest,
  PolicyCancellationRequestPayload,
  Submission,
  SubmissionResponse,
} from '../../types';
import {
  DwellingInformation,
  BasicAddressDetails,
  PolicyDwelling,
  OwlAddPolicyData,
  Scenario,
  AddClaimsPayload,
  UpdateDeclarationPagePayload,
} from '../../types/owl';
import * as OWL from '../../owl';

export class ClientClient extends AbstractClient {
  fetch = this.createGetHandler<
    ClientResponse,
    { args: { uuid: string }; query?: { include?: string } }
  >(({ uuid }) => `/${uuid}`, { include: '*' });

  create = this.createPayloadHandler<
    ClientResponse,
    { payload: CreateClientRequest }
  >('POST', '/');

  update = this.createPayloadHandler<
    ClientResponse,
    { args: { uuid: string }; payload: Partial<PersonalInfo> }
  >('PUT', ({ uuid }) => `/${uuid}`);

  authenticatedUpdate = this.createPayloadHandler<
    ClientResponse,
    { args: { uuid: string }; payload: AuthUpdateClientPayload }
  >('PATCH', ({ uuid }) => `/${uuid}/update`);

  fetchDrivers = this.createGetHandler<
    DriversResponse,
    { args: { uuid: string } }
  >(({ uuid }) => `/${uuid}/drivers`);

  createDrivers = this.createPayloadHandler<
    DriversResponse,
    { args: { uuid: string }; payload: Driver[] | undefined }
  >('POST', ({ uuid }) => `/${uuid}/drivers`);

  uploadDocument = this.createPayloadHandler<
    DocumentUploadResponse,
    { args: { clientUuid: string }; payload: FormData }
  >(
    'POST',
    ({ clientUuid }) => `/${clientUuid}/manual-declaration-page-upload`,
  );

  getCustomLinks = this.createGetHandler<
    OwlResponse<Record<string, AttributionSource>>,
    { query: { includeArchived?: boolean } }
  >('/attribution-sources');

  notifyOfDataEntryPrompt = this.createPayloadHandler<
    DocumentUploadResponse,
    { args: { clientUuid: string } }
  >(
    'POST',
    ({ clientUuid }) => `/${clientUuid}/prompted-for-manual-data-entry`,
  );

  notifyOfDataEntryCompletion = this.createPayloadHandler<
    DocumentUploadResponse,
    { args: { clientUuid: string } }
  >('POST', ({ clientUuid }) => `/${clientUuid}/manual-data-entry-complete`);

  //=========== Submission ==========//

  getSubmissions = this.createGetHandler<
    OwlResponse<Submission[]>,
    { args: { uuid: string } }
  >(({ uuid }) => `/${uuid}/submissions`);

  createSubmission = this.createPayloadHandler<
    SubmissionResponse,
    {
      args: { uuid: string };
      payload: { carrierSlug: string; carrierName?: string };
    }
  >('POST', ({ uuid }) => `/${uuid}/submission`);

  addAdditionalFields = this.createPayloadHandler<
    SubmissionResponse,
    {
      args: { clientId: string; submissionId: string };
      payload: { additionalFields: Record<string, string> };
    }
  >(
    'POST',
    ({ clientId, submissionId }) =>
      `/${clientId}/submission/${submissionId}/add-additional-fields`,
  );

  addMetadata = this.createPayloadHandler<
    SubmissionResponse,
    {
      args: { clientId: string; submissionId: string };
      payload: { metadata: Record<string, any> };
    }
  >(
    'POST',
    ({ clientId, submissionId }) =>
      `/${clientId}/submission/${submissionId}/add-metadata`,
  );

  updateSubmission = this.createPayloadHandler<
    SubmissionResponse,
    {
      args: { clientUuid: string; submissionUuid: string };
      payload: { carrierSlug: string };
    }
  >(
    'PATCH',
    ({ clientUuid, submissionUuid }) =>
      `/${clientUuid}/submission/${submissionUuid}`,
  );

  deleteSubmission = this.createPayloadHandler<
    SubmissionResponse,
    { args: { clientUuid: string; submissionId: string } }
  >(
    'DELETE',
    ({ clientUuid, submissionId }) =>
      `/${clientUuid}/submission/${submissionId}`,
  );

  async createLoginAttempt(data: OWL.CreateLoginAttempt) {
    return this.client.post(
      `/${data.clientId}/submission/${data.submissionId}/login-attempt`,
      {
        ...data.data,
        uuid: data.loginAttemptId,
        credentialStatus: data.status,
        startTime: data.startTime,
        endTime: data.endTime,
        metadata: data.metadata,
        generation: data.generation,
      },
    );
  }

  /** `generation` (INS-1391) is omitted when undefined, so Owl stays unfenced. */
  updateSubmissionState(
    clientId: string,
    submissionId: string,
    state: OWL.SubmissionStatus,
    generation?: number,
  ) {
    return this.client.post(
      `/${clientId}/submission/${submissionId}/update-state`,
      {
        state,
        generation,
      },
    );
  }

  /** INS-1391: credentials of one exact login attempt (internal token only). */
  async getLoginAttemptCredentials(
    clientId: string,
    submissionId: string,
    loginAttemptId: string,
  ) {
    const { data } = await this.client.get<{
      username: string;
      password: string | null;
      additionalFields: Record<string, string> | null;
    }>(
      `/${clientId}/submission/${submissionId}/login-attempt/${loginAttemptId}/credentials`,
    );
    return data;
  }

  advanceValidationGeneration(
    clientId: string,
    submissionId: string,
    generation: number,
  ) {
    return this.client.post(
      `/${clientId}/submission/${submissionId}/validation-generation`,
      { generation },
    );
  }

  //======== Dwellings ========//

  createDwelling = this.createPayloadHandler<
    OwlResponse<DwellingInformation>,
    { args: { clientId: string }; payload: BasicAddressDetails }
  >('POST', ({ clientId }) => `/${clientId}/dwellings`);

  getDwellings = this.createGetHandler<
    OwlResponse<DwellingInformation[]>,
    { args: { clientId: string } }
  >(({ clientId }) => `/${clientId}/dwellings`);

  patchDwelling = this.createPayloadHandler<
    OwlResponse<PolicyDwelling & { uuid: string }>,
    {
      args: { clientId: string; dwellingId: string };
      payload: Partial<PolicyDwelling>;
    }
  >(
    'PATCH',
    ({ clientId, dwellingId }) => `${clientId}/dwellings/${dwellingId}`,
  );

  deleteDwelling = this.createPayloadHandler<
    OwlResponse<string>,
    {
      args: { clientId: string; dwellingId: string };
      payload: Partial<DwellingInformation>;
    }
  >(
    'DELETE',
    ({ clientId, dwellingId }) => `${clientId}/dwellings/${dwellingId}`,
  );

  haveNoDwellings = this.createPayloadHandler<
    OwlResponse<null>,
    { args: { clientId: string } }
  >('POST', ({ clientId }) => `${clientId}/dwellings/no-dwellings-for-client`);

  //=========== Drivers ============//

  getDrivers = this.createGetHandler<
    OwlResponse<Driver[]>,
    { args: { clientId: string } }
  >(({ clientId }) => `/${clientId}/drivers`);

  createDriver = this.createPayloadHandler<
    OwlResponse<Driver>,
    { args: { clientId: string }; payload: Partial<Driver> }
  >('POST', ({ clientId }) => `/${clientId}/drivers`);

  patchDriver = this.createPayloadHandler<
    OwlResponse<Driver>,
    {
      args: { clientId: string; driverId: string };
      payload: Partial<Driver>;
    }
  >('PATCH', ({ clientId, driverId }) => `${clientId}/drivers/${driverId}`);

  deleteDriver = this.createPayloadHandler<
    OwlResponse<string>,
    {
      args: { clientId: string; driverId: string };
      payload: Partial<Driver>;
    }
  >('DELETE', ({ clientId, driverId }) => `${clientId}/drivers/${driverId}`);

  haveNoDrivers = this.createPayloadHandler<
    OwlResponse<null>,
    { args: { clientId: string } }
  >('POST', ({ clientId }) => `${clientId}/drivers/no-drivers-for-client`);

  createCancellationRequest = this.createPayloadHandler<
    OwlResponse<PolicyCancellationRequest>,
    {
      args: { clientId: string };
      payload: PolicyCancellationRequestPayload;
    }
  >('POST', ({ clientId }) => `${clientId}/policy-cancellation-request`);

  async manualDocumentUpload(
    clientId: string,
    carrierSlug: string,
    files: File[],
    submissionId?: string,
    /** Required by owl when carrierSlug is "other" — the name the client typed. */
    carrierName?: string,
  ) {
    const formData = new FormData();
    formData.append('carrierSlug', carrierSlug);
    for (const file of files) {
      formData.append('files[]', file, file.name);
    }
    if (submissionId) {
      formData.append('uuid', submissionId);
    }
    if (carrierName) {
      formData.append('carrierName', carrierName);
    }
    // Owl returns a Laravel resource, so the body is wrapped in a `data`
    // envelope: { data: DocumentUpload }. Unwrap it to return the inner payload.
    const res = await this.client.post<
      FormData,
      OwlResponse<{ data: DocumentUpload }>
    >(`/${clientId}/manual-declaration-page-upload`, formData);
    return res.data.data;
  }

  async addPolicyToSubmission(
    clientId: string,
    submissionId: string,
    policy: OwlAddPolicyData,
  ) {
    return this.client.post(
      `/${clientId}/submission/${submissionId}/policy`,
      policy,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
  }

  async addPolicyToSubmissionOLD(
    clientId: string,
    submissionId: string,
    formData: FormData,
  ) {
    return this.client.post(
      `/${clientId}/submission/${submissionId}/policies`,
      formData,
      { headers: formData.getHeaders() },
    );
  }

  async addClaimsToSubmission(
    clientId: string,
    submissionId: string,
    payload: AddClaimsPayload,
  ) {
    return this.client.post(
      `/${clientId}/submission/${submissionId}/claims`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
  }

  async updateDeclarationPage(
    clientId: string,
    submissionId: string,
    declarationPageUuid: string,
    payload: UpdateDeclarationPagePayload,
  ) {
    return this.client.patch(
      `/${clientId}/submission/${submissionId}/declaration-page/${declarationPageUuid}`,
      payload,
      { headers: { 'Content-Type': 'application/json' } },
    );
  }

  createRandom = async (agentSlug: string) => {
    const payload = {
      firstName: 'test',
      lastName: 'tester',
      email: 'test@tester.com',
      phone: '6039216202',
      agentSlug,
      insuranceLine: LineType.Personal,
    };

    const data = await this.create({ payload });
    return data.data;
  };

  async addDocumentToSubmission(
    clientId: string,
    submissionId: string,
    document: FormData,
  ) {
    return this.client.post(
      `/${clientId}/submission/${submissionId}/policy-documents`,
      document,
      { headers: document.getHeaders() },
    );
  }

  async completeAutomationForSubmission(
    clientId: string,
    submissionId: string,
    messages: string[],
    policyIds: string[],
    scenarios?: Scenario[],
  ) {
    await this.client.post(
      `/${clientId}/submission/${submissionId}/automation-complete`,
      {
        messages,
        policyIds,
        automationScenarios: scenarios ?? [],
      },
    );
  }

  async saveAgentQuestionResponse(
    clientId: string,
    question: string,
    response: string,
  ) {
    await this.client.put(`/${clientId}/additional-question-response`, {
      question,
      response,
    });
  }

  //   cancel client

  createAddress = this.createPayloadHandler<
    OwlResponse<ClientAddress>,
    { args: { clientId: string }; payload: BasicAddressDetails }
  >('POST', ({ clientId }) => `/${clientId}/addresses`);

  getAddresses = this.createGetHandler<
    OwlResponse<ClientAddress[]>,
    { args: { clientId: string } }
  >(({ clientId }) => `/${clientId}/addresses`);

  getSpecificAddress = this.createGetHandler<
    OwlResponse<ClientAddress>,
    { args: { clientId: string; addressId: string } }
  >(({ clientId, addressId }) => `/${clientId}/addresses/${addressId}`);
}
